import { useMemo } from 'react';
import { XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type {
  LabelView,
  TaskView,
  WorkflowView,
} from '@/lib/api/generated/model';
import { useCanWork } from '@/features/workspaces/canWork';
import { FOCUS_RING } from '@/shared/components/pillClasses';

import {
  clearFilters,
  filterChips,
  peopleOfTasks,
  type TaskListView,
} from '../listView';

export interface TaskListChipsProps {
  view: TaskListView;
  onViewChange: (next: TaskListView) => void;
  registry: readonly LabelView[];
  workflows: readonly WorkflowView[];
  tasks: readonly TaskView[];
}

/**
 * What the list is narrowed to, one value per chip (spec 113 US1).
 *
 * Before this row, two states read "State: 2" and four filters hid behind
 * "More filters (4)": the page could not be read without opening menus. Each
 * chip says one value in the menus' own words and removes exactly that value;
 * what "exactly" means lives in `filterChips`, which hands every chip the view
 * without it, so this component knows nothing about any filter.
 *
 * Absent — not empty, not reserved — when nothing is active (FR-001).
 */
export function TaskListChips({
  view,
  onViewChange,
  registry,
  workflows,
  tasks,
}: TaskListChipsProps) {
  const canWork = useCanWork();
  const chips = useMemo(
    () =>
      filterChips(view, {
        labels: registry,
        workflows,
        people: peopleOfTasks(tasks, undefined).everyone,
        canWork,
      }),
    [view, registry, workflows, tasks, canWork],
  );

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <ul
        aria-label="Active filters"
        className="flex min-w-0 flex-wrap items-center gap-1.5"
      >
        {chips.map((chip) => (
          <li
            key={chip.id}
            className="border-selected-border bg-selected text-foreground inline-flex h-6 max-w-72 min-w-0 items-center gap-0.5 rounded-md border pr-0.5 pl-2 text-2xs"
          >
            <span className="truncate" title={chip.label}>
              {chip.label}
            </span>
            <button
              type="button"
              aria-label={`Remove ${chip.label}`}
              onClick={() => {
                onViewChange(chip.without);
              }}
              className={`text-muted-foreground hover:bg-accent hover:text-foreground inline-flex size-5 shrink-0 items-center justify-center rounded-sm ${FOCUS_RING}`}
            >
              <XIcon aria-hidden="true" className="size-3" />
            </button>
          </li>
        ))}
      </ul>
      {/* One chip is its own clear; from two, a way to drop them all (FR-003). */}
      {chips.length > 1 ? (
        <Button
          variant="ghost"
          size="xs"
          onClick={() => {
            onViewChange(clearFilters(view));
          }}
        >
          Clear all
        </Button>
      ) : null}
    </div>
  );
}
